// URLS MIXIN

import {
   insertUrlParam,
   removeUrlParam,
} from '@/js/global/helpers/url';

import {
   SCENARIO_MENUS_LISTING_PAGE,
   SCENARIO_SELECTION_PAGE,
   SCENARIO_DELIVERY_PAGE,
   SCENARIO_OVERVIEW_PAGE,
   SCENARIO_CONFIRMATION_PAGE,
} from '@/js/funnel/_config/funnel'

/**
 * URL query param for the confirmation page's template to query for the order by this param's
 * value. The value is the full order number.
 *
 * @type {string}
 */
export const ORDER_NUMBER_PARAM = 'number';


/**
 * URL query param for detecting if the request is a cancelled payment. This can happen if, for
 * example, Stripe returns the customer back to the Overview page because they failed to confirm
 * the transaction on their phone.
 *
 * NOTE: this must match the constant in this file:
 * ./cms/modules/mymy/src/helpers/ControllerHelper.php
 *
 * @type {string}
 */
export const CANCELLED_QUERY_PARAM_KEY = 'cancelled';

/**
 * If this arbitrary value is found in the URL, then the we know the customer was returned to
 * the Overview page because of an issue trying to process their payment.
 *
 * @type {string}
 */
export const CANCELLED_QUERY_PARAM_VALUE = 'XBqSTJKF6L';

/**
 * The mixin for handling URLs in the funnel.
 *
 * @type {{data(): {urlParams: {}}, computed: {nextPageData(): (*|null), nextPageUrl():
 *    (null|null), previousPageUrl(): (null|null), previousPageData(): (*|null)}}}
 */
export const UrlsMixin = {
   data() {
      return {
         urlParams: {},
      }
   },
   computed: {
      /**
       * Get urls used to redirect the user to different pages around the funnel.
       *
       * @return {*}
       */
      urls() {
         return window.mymy.urls;
      },

      /**
       * An array of all query params above that are reserved for operating the funnel.
       *
       * @return {[string, string, string, string]}
       */
      reservedQueryParams() {
         return [
            ORDER_NUMBER_PARAM,
            CANCELLED_QUERY_PARAM_KEY,
         ];
      },

      /**
       * The URL to the "Menus Listing" page (the very beginning of the funnel).
       *
       * @return {string}
       */
      menusListingPageUrl() {
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_MENUS_LISTING_PAGE) {
               return stepObject.url;
            }
         }
         return '';
      },

      /**
       * The URL to the selected menu's "Items Selection" page.
       *
       * @return {string}
       */
      itemsSelectionPageUrl() {
         let url = '';
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_SELECTION_PAGE) {
               url = `${stepObject.url}#/step-${stepObject.sectionIndex}`;
            }
         }

         return url;
      },

      /**
       * The URL to the "Delivery Details" checkout page.
       *
       * @return {string}
       */
      checkout1PageUrl() {
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_DELIVERY_PAGE) {
               return this.stripHashPrefixFromUrl(stepObject.url);
            }
         }
         return '';
      },

      /**
       * The URL to the "Cart Overview" checkout page.
       *
       * @return {string}
       */
      checkout2PageUrl() {
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_OVERVIEW_PAGE) {
               return stepObject.url;
            }
         }
         return '';
      },

      /**
       * The URL to the "Order Confirmation" page (the final page the funnel).
       *
       * @return {string}
       */
      confirmationPageUrl() {
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_CONFIRMATION_PAGE) {
               return stepObject.url;
            }
         }
         return '';
      },


      /**
       * A `ProgressStepModel` object of the PREVIOUS page (not step! because a menu's section
       * could be a step without changing the URL).
       *
       * @return {null|object}
       * @see ./cms/modules/mymy/src/models/funnel/ProgressStepModel.php
       */
      previousPageData() {
         for (const stepObject of this.urls) {
            if (stepObject.isPreviousUrl) {
               return {...stepObject};
            }
         }
         return null;
      },

      /**
       * The URL for the PREVIOUS page (not step!), including any dynamic query params appended.
       *
       * @return {null|string}
       */
      previousPageUrl() {
         let url = this.previousPageData.url ?? null;
         if (! url) {
            return this.menusListingPageUrl;
         }

         return url;
      },

      /**
       * A `ProgressStepModel` object of the NEXT page (not step! because a menu's section
       * could be a step without changing the URL).
       *
       * @return {null|object}
       * @see ./cms/modules/mymy/src/models/funnel/ProgressStepModel.php
       */
      nextPageData() {
         for (const stepObject of this.urls) {
            if (stepObject.isNextUrl) {
               return {...stepObject};
            }
         }
         return null;
      },

      /**
       * The URL for the NEXT page (not step!), including any dynamic query params appended.
       *
       * @return {null|string}
       */
      nextPageUrl() {
         let url = this.nextPageData.url ?? null;
         if (! url) {
            return this.menusListingPageUrl;
         }

         return url;
      },
   },

   methods: {
      /**
       * Combine any non-reserved query param from the URL with params already saved on the
       * cart, which can then be used for re-saving to the customer's cart.
       *
       * @return {URLSearchParams}
       */
      getQueryParamsForSaving() {
         // params in the current URL
         const urlParams = new URLSearchParams(window.location.search);

         // add params from the cart, and overwrite any matching params currently on the URL
         for (const key in this?.urlParams?.utm?.params) {
            if(!key){
               continue;
            }
            urlParams.set(key, this.urlParams.utm.params[key]);
         }

         // remove funnel related query parameters from the saved data
         this.reservedQueryParams.forEach((value, key) => {
            urlParams.delete(value);
         });

         urlParams.sort();

         return urlParams;
      },

      /**
       * Add a query param and its value to the URL.
       *
       * @param key {string}
       * @param value {string}
       * @see @/js/global/helpers/url.js
       */
      insertUrlParam: (key, value) => insertUrlParam(key, value),

      /**
       * Remove a query param from the URL by its key.
       *
       * @param key {string}
       * @see @/js/global/helpers/url.js
       */
      removeUrlParam: key => removeUrlParam(key),

      /**
       * Scroll the user to the top of the page.
       */
      scrollToTop() {
         this.$scrollTo('body');
      },

      stripHashPrefixFromUrl(url) {
          // Regular expression pattern to match the beginning of a URL
          var pattern = /^.*?(http:\/\/|https:\/\/)/;

          // Use replace method to remove the matched pattern from the URL
          var strippedUrl = url.replace(pattern, '$1');

          return strippedUrl;
      },


      /**
       * Send the customer to a different URL.
       *
       * This will likely be the url set in this.redirect.url when the Vue instance mounts.
       * Custom URLs are welcome too though.
       *
       * @param string url
       */
      redirectUser(url) {
         if (typeof url === 'string' && url) {
            window.onbeforeunload = null;
            window.location.href = url;
         }
         else if (this.nextPageUrl) {
            window.onbeforeunload = null;
            window.location.href = this.nextPageUrl;
         }
      },

      /**
       * Reload the page the customer is currently on.
       */
      reloadPage() {
         window.onbeforeunload = null;
         location.reload();
      },

      /**
       * Get the URL to the Confirmation page (the final step of the funnel).
       *
       * @param hash {boolean}
       * @return {string}
       * @throws Error
       * @see \mwc\mymy\services\FunnelPagesService::getUrlParamsForStoreData
       */
      getFinalCheckoutUrl(hash = false) {
         // this should be coming from the customer's cart
         try {
            return this.urlParams.confirmation[(hash ? 'hash' : 'url')];
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }

         // if no cart, construct it manually
         for (const stepObject of this.urls) {
            if (stepObject.isFinalStep) {
               return stepObject.url + '?' + ORDER_NUMBER_PARAM + '=' + this.cart.number;
            }
         }

         throw new Error("Final checkout page URL couldn't be constructed.");
         return '';
      },

      /**
       * Get the URL to the Confirmation page (the final step of the funnel).
       *
       * @param hash {boolean}
       * @return {string}
       * @throws Error
       * @see \mwc\mymy\services\FunnelPagesService::getUrlParamsForStoreData
       */
      getCancelledCheckoutUrl(hash = false) {
         // this should be coming from the customer's cart
         try {
            return this.urlParams.cancel[(hash ? 'hash' : 'url')];
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }

         // if no cart, construct it manually
         for (const stepObject of this.urls) {
            if (stepObject.scenario === SCENARIO_OVERVIEW_PAGE) {
               return stepObject.url + '?' + CANCELLED_QUERY_PARAM_KEY + '=' + CANCELLED_QUERY_PARAM_VALUE;
            }
         }

         throw new Error("Checkout cancellation URL couldn't be constructed.");
         return '';
      },
   },
};